/**
 * Estado do roteiro, compartilhado entre as telas do módulo.
 *
 * Existe por um motivo específico: a conclusão é UNIFICADA (PRD §14). O `✓`
 * da lista e o botão do detalhe têm de produzir exatamente o mesmo efeito —
 * status, histórico e toast. Com estado local em cada tela, seriam dois
 * caminhos e, portanto, duas oportunidades de divergir.
 *
 * Nenhuma regra vive aqui: tudo delega para `@nv/core`, que roda igual no
 * servidor. Este arquivo só orquestra e registra histórico.
 */

import {
  type Activity,
  reorderActivities,
  toggleActivityCompletion,
  uuidv7,
} from '@nv/core';
import type { Mutation } from '@nv/offline';
import { create } from 'zustand';

import { loadItinerary, localDb, type PersistedItinerary, saveItinerary } from './persistence';

export interface HistoryEntry {
  id: string;
  authorId: string;
  description: string;
  /** Instante real. O rótulo relativo é derivado na exibição. */
  at: number;
}

interface ItineraryState {
  activities: Activity[];
  history: HistoryEntry[];
  /** Quem está usando o app. Virá da sessão quando a auth estiver ligada. */
  currentMemberId: string;
  /** Versão do conjunto ordenado — base do conflito de reordenação. */
  reorderVersion: number;
  dayId: string;
  toast: string | null;
  /** Marcado quando a carga inicial terminou, para não salvar por cima. */
  ready: boolean;

  hydrate: (data: {
    activities: Activity[];
    history: HistoryEntry[];
    currentMemberId: string;
    reorderVersion?: number;
    dayId?: string;
  }) => void;

  /** Único caminho de conclusão do módulo. */
  toggleCompletion: (activityId: string) => void;
  reorder: (activityId: string, toIndex: number) => void;
  /** Edição explícita. O horário só muda por aqui — nunca por reordenação. */
  /**
   * Resolvem quando a mutação está DURÁVEL (outbox + estado no aparelho).
   * Quem navega depois de salvar espera por isto: sair da tela antes de a
   * escrita terminar perde a alteração numa recarga.
   */
  updateActivity: (activityId: string, patch: Partial<Activity>) => Promise<void>;
  createActivity: (activity: Activity) => Promise<void>;
  deleteActivity: (activityId: string) => Promise<void>;

  /** `duracaoMs`: o padrão (2,6 s) serve a confirmações na mesma tela; uma que atravessa navegação pede mais. */
  showToast: (message: string, duracaoMs?: number) => void;
  clearToast: () => void;
}

const novoId = (): string =>
  globalThis.crypto?.randomUUID?.() ?? `h-${Date.now()}-${Math.random()}`;

export const useItinerary = create<ItineraryState>((set, get) => {
  /** Toda mutação registra ator e instante — o histórico não é opcional. */
  const registrar = (description: string) => {
    set((s) => ({
      history: [
        { id: novoId(), authorId: s.currentMemberId, description, at: Date.now() },
        ...s.history,
      ],
    }));
  };

  /**
   * Persiste estado otimista E enfileira a mutação, sempre juntos.
   *
   * Salvar só o estado faria a alteração parecer aplicada e nunca chegar ao
   * servidor. Enfileirar só a mutação a faria sumir da tela até sincronizar.
   */
  const enfileirar = async (
    entity: Mutation['entity'],
    operation: Mutation['operation'],
    payload: unknown,
    baseVersion?: number,
  ) => {
    const s = get();
    if (!s.ready || !s.dayId) {
      // Não pode acontecer: toda tela que muta carrega o dia antes. Se
      // acontecer, é defeito — e defeito se vê, não se engole.
      throw new Error('Mutação do roteiro sem dia carregado.');
    }

    const db = await localDb();
    await db.outbox.put({
      // O mesmo UUIDv7 serve de id da mutação e de Idempotency-Key.
      mutationId: uuidv7(),
      entity,
      operation,
      payload,
      ...(baseVersion === undefined ? {} : { baseVersion }),
      createdAt: Date.now(),
      retryCount: 0,
      status: 'pending',
    });

    await saveItinerary({
      activities: s.activities,
      history: s.history,
      currentMemberId: s.currentMemberId,
      reorderVersion: s.reorderVersion,
      dayId: s.dayId,
    });

    // Avisa a interface para recontar pendências sem acoplar os dois stores.
    window.dispatchEvent(new CustomEvent('nv:outbox-changed'));
  };

  const comToast = (message: string, duracaoMs = 2600) => {
    set({ toast: message });
    window.setTimeout(() => {
      if (get().toast === message) set({ toast: null });
    }, duracaoMs);
  };

  return {
    activities: [],
    history: [],
    currentMemberId: '',
    reorderVersion: 0,
    dayId: '',
    toast: null,
    ready: false,

    hydrate: (data) => set({ ...data, ready: true }),

    toggleCompletion: (activityId) => {
      const activity = get().activities.find((a) => a.id === activityId);
      if (!activity) return;

      const resultado = toggleActivityCompletion(activity);
      if (!resultado) return;

      set((s) => ({
        activities: s.activities.map((a) => (a.id === activityId ? resultado.activity : a)),
      }));
      registrar(
        `${resultado.historyAction === 'concluiu' ? 'Concluiu' : 'Reabriu'} "${activity.title}"`,
      );
      void enfileirar(
        'activity',
        'update',
        { activityId: activityId, dayId: get().dayId, patch: { status: resultado.activity.status } },
        (activity as Activity & { version?: number }).version ?? 1,
      );
      comToast(resultado.toast);
    },

    reorder: (activityId, toIndex) => {
      const ordenadas = [...get().activities].sort((a, b) => a.sortOrder - b.sortOrder);
      const movida = ordenadas.find((a) => a.id === activityId);
      if (!movida) return;

      const novaOrdem = reorderActivities(ordenadas, activityId, toIndex);
      set({ activities: novaOrdem });
      registrar(`Reordenou "${movida.title}"`);
      void enfileirar('itinerary_day', 'update', {
        dayId: get().dayId,
        reorder: {
          // Versão do CONJUNTO: duas pessoas reordenando ao mesmo tempo
          // produzem conflito de dia, não de atividade.
          baseReorderVersion: get().reorderVersion,
          orderedActivityIds: novaOrdem.map((a) => a.id),
        },
      });
      comToast('Ordem salva · o horário viaja com a atividade');
    },

    updateActivity: async (activityId, patch) => {
      const anterior = get().activities.find((a) => a.id === activityId);
      if (!anterior) return;

      set((s) => ({
        activities: s.activities.map((a) => (a.id === activityId ? { ...a, ...patch } : a)),
      }));

      // O histórico distingue mudança de horário das demais: é a alteração
      // que mais afeta quem já se organizou em torno dela.
      if (patch.startTime && patch.startTime !== anterior.startTime) {
        registrar(`Alterou horário de ${anterior.startTime} para ${patch.startTime}`);
      } else {
        registrar(`Editou "${anterior.title}"`);
      }
      comToast('Atividade atualizada · registrada no histórico');
      await enfileirar(
        'activity',
        'update',
        // `dayId` não vai ao servidor (o corpo é só o patch): serve para o
        // aceite achar o estado guardado do dia certo.
        { activityId, dayId: get().dayId, patch },
        (anterior as Activity & { version?: number }).version ?? 1,
      );
    },

    createActivity: async (activity) => {
      set((s) => ({ activities: [...s.activities, activity] }));
      registrar(`Adicionou "${activity.title}"`);
      comToast('Atividade adicionada ao roteiro');
      await enfileirar('activity', 'create', {
        create: {
          id: activity.id,
          itineraryDayId: get().dayId,
          title: activity.title,
          startTime: activity.startTime,
          // Fim e "dia inteiro" (PO, 22/09/2026): o payload é explícito, então
          // campo novo precisa entrar aqui — senão some entre a tela e a API.
          endTime: activity.endTime ?? null,
          allDay: activity.allDay ?? false,
          locationName: activity.locationName ?? null,
          placeId: activity.placeId ?? null,
          placeAddress: activity.placeAddress ?? null,
          latitude: activity.latitude ?? null,
          longitude: activity.longitude ?? null,
          notes: activity.notes ?? null,
          alertMinutesBefore: activity.alertMinutesBefore ?? null,
          sortOrder: activity.sortOrder,
          participantMemberIds: activity.participantMemberIds ?? [],
        },
      });
    },

    deleteActivity: async (activityId) => {
      const alvo = get().activities.find((a) => a.id === activityId);
      if (!alvo) return;
      set((s) => ({ activities: s.activities.filter((a) => a.id !== activityId) }));
      registrar(`Excluiu "${alvo.title}"`);
      comToast('Atividade excluída');
      // Antes só sumia da tela deste aparelho e voltava no próximo carregamento
      // (teste físico, item 12): a exclusão precisa ir ao servidor como as
      // outras mutações, com a versão que o aparelho viu.
      await enfileirar(
        'activity',
        'delete',
        { activityId, dayId: get().dayId },
        (alvo as Activity & { version?: number }).version ?? 1,
      );
    },


    showToast: comToast,
    clearToast: () => set({ toast: null }),
  };
});

/**
 * O servidor aceitou uma mutação do roteiro: traz a versão nova para o
 * estado local — em memória, se o dia é o aberto, e no estado guardado do
 * aparelho sempre. É o que impede a próxima edição de sair com a versão
 * velha e conflitar consigo mesma.
 */
export async function aplicarAceite(mutation: Mutation, body: unknown): Promise<void> {
  const corpo = (body ?? {}) as { version?: number; reorderVersion?: number };
  const payload = (mutation.payload ?? {}) as {
    activityId?: string;
    dayId?: string;
    create?: { id: string; itineraryDayId: string };
  };

  let dayId: string | undefined;
  let mudar: ((estado: PersistedItinerary) => PersistedItinerary) | null = null;

  if (mutation.entity === 'activity' && mutation.operation === 'delete') {
    // Aceita: some também do estado guardado, mesmo que uma mesclagem com o
    // servidor a tenha trazido de volta enquanto a fila esperava.
    dayId = payload.dayId;
    const activityId = payload.activityId;
    mudar = (estado) => ({ ...estado, activities: estado.activities.filter((a) => a.id !== activityId) });
  } else if (mutation.entity === 'activity' && typeof corpo.version === 'number') {
    const activityId = mutation.operation === 'create' ? payload.create?.id : payload.activityId;
    dayId = mutation.operation === 'create' ? payload.create?.itineraryDayId : payload.dayId;
    if (!activityId) return;
    const versao = corpo.version;
    mudar = (estado) => ({
      ...estado,
      activities: estado.activities.map((a) =>
        a.id === activityId ? ({ ...a, version: versao } as Activity) : a,
      ),
    });
  } else if (mutation.entity === 'itinerary_day' && typeof corpo.reorderVersion === 'number') {
    dayId = payload.dayId;
    const versao = corpo.reorderVersion;
    mudar = (estado) => ({ ...estado, reorderVersion: versao });
  }
  if (!dayId || !mudar) return;

  const s = useItinerary.getState();
  if (s.ready && s.dayId === dayId) {
    const novo = mudar({
      activities: s.activities,
      history: s.history,
      currentMemberId: s.currentMemberId,
      reorderVersion: s.reorderVersion,
      dayId,
    });
    useItinerary.setState({ activities: novo.activities, reorderVersion: novo.reorderVersion });
    await saveItinerary(novo);
    return;
  }

  const guardado = await loadItinerary(dayId);
  if (guardado) await saveItinerary(mudar(guardado));
}
